import type { RunResult } from "../sdk";
import { showTip, hideTip, infoDot } from "./tooltip";

// Results panel — dressed in the v1 design language (cards, stat rows, status pills) and
// drawing a real canvas histogram with hover tooltips. Renders the headline result, summary
// stats, the per-chunk distribution, the verifiable/unverifiable tier badge, and the
// reproducible hashes. Pure DOM (no React / no chart lib) so the playground stays
// zero-dependency. Info-dot tooltips are handled by the shared, globally-delegated tooltip
// layer (./tooltip) so they work anywhere — here and in the parameter controls.

function summarise(samples: number[]) {
  if (samples.length === 0) return null;
  const sorted = [...samples].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = sorted.reduce((a, b) => a + b, 0) / n;
  const variance = sorted.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const stdev = Math.sqrt(variance);
  // Standard error of the mean across chunk estimates, and the 95% CI half-width (±1.96·SE).
  // This is the honest "how settled is this estimate" number (plan §4.3) — distinct from the
  // live preview band, which is computed in arrival order; here we have every chunk.
  const se = n > 1 ? stdev / Math.sqrt(n) : 0;
  const ci95 = 1.96 * se;
  const q = (p: number) => sorted[Math.min(n - 1, Math.floor(p * n))]!;
  return { mean, stdev, se, ci95, min: sorted[0]!, max: sorted[n - 1]!, p50: q(0.5) };
}

const fmt = (v: number, p = 6) =>
  Math.abs(v) >= 1e6 || (v !== 0 && Math.abs(v) < 1e-4)
    ? v.toExponential(4)
    : v.toPrecision(p);

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

// Plain-language explanations for each item in the panel. Shown on the ⓘ affordance.
const TIPS = {
  result: "The headline answer of this Monte Carlo run — the average over all N samples. More iterations (N) tighten the estimate.",
  ci: "95% confidence interval on the estimate (±1.96 × standard error across the per-chunk estimates). Roughly: re-running with a fresh seed lands inside this range about 19 times in 20. More iterations (N) shrink it.",
  se: "Standard error of the estimate — the standard deviation of the per-chunk estimates divided by √(chunks). It scales like 1/√N, so to halve it you run ~4× the iterations.",
  tier:
    "<strong>Verifiable</strong>: reproduces bit-for-bit across browser engines (Chrome / Firefox / Safari), because it uses only IEEE-specified arithmetic and the pinned fdlibm math library. <strong>Engine-specific</strong>: only guaranteed identical within the same browser engine.",
  meta: "N = total samples · seed = the string that seeds the RNG (same kernel + seed + N ⇒ identical result) · ms = wall-clock time · chunks = how the work was split to run in parallel across your CPU cores.",
  mean: "Average of the per-chunk estimates. Each chunk is its own mini-run, so this is the mean of those sub-answers.",
  stdev: "Standard deviation across the per-chunk estimates — how much the sub-runs disagree. Smaller = a more stable, confident estimate.",
  p50: "Median of the per-chunk estimates (the middle value).",
  min: "Smallest per-chunk estimate.",
  max: "Largest per-chunk estimate.",
  hist: "Distribution of the per-chunk estimates. Each bar is a value range; its height is how many chunks landed in that range. A tall, narrow peak means the chunks agreed; a wide spread means a noisier estimate. Hover a bar for details.",
  resultBits: "The exact answer's IEEE-754 bits, in hex — the fingerprint you compare against someone else's run to prove byte-identical results.",
  kernelHash: "A content hash identifying which kernel (which simulation) produced this result.",
  substrate: "The reproducibility version triple: SDK version · math-shim · fdlibm bytes. Bitwise reproducibility is promised within the same substrate, so it's recorded with the run.",
} as const;

function info(key: keyof typeof TIPS): string {
  // The shared tooltip layer (./tooltip) shows data-tip on hover / focus / click.
  return infoDot(escapeHtml(TIPS[key]));
}

// ── Histogram ──────────────────────────────────────────────────────────────
interface Bin {
  x: number; // px, left edge of the bar
  w: number; // px, bar width
  count: number;
  lo: number; // value range start
  hi: number; // value range end
}
interface HistGeom {
  bins: Bin[];
  padX: number;
  padY: number;
  plotH: number;
  cssW: number;
  cssH: number;
}

function computeBins(samples: number[], min: number, max: number, nBins: number): { lo: number; hi: number; count: number }[] {
  const span = max - min || 1;
  const out = Array.from({ length: nBins }, (_, i) => ({
    lo: min + (span * i) / nBins,
    hi: min + (span * (i + 1)) / nBins,
    count: 0,
  }));
  for (const v of samples) {
    const idx = Math.min(nBins - 1, Math.floor(((v - min) / span) * nBins));
    out[idx]!.count++;
  }
  return out;
}

// Draw the histogram and return its bin geometry so the hover handler can hit-test bars.
function drawHistogram(canvas: HTMLCanvasElement, samples: number[], mean: number, highlight = -1): HistGeom | null {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 600;
  const cssH = canvas.clientHeight || 180;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  if (samples.length < 2) {
    ctx.fillStyle = "rgba(30,31,31,0.4)";
    ctx.font = "13px ui-sans-serif, system-ui, sans-serif";
    ctx.fillText("Not enough chunks to plot a distribution.", 12, cssH / 2);
    return null;
  }

  const min = Math.min(...samples);
  const max = Math.max(...samples);
  const span = max - min || 1;
  const nBins = Math.min(28, Math.max(6, Math.round(Math.sqrt(samples.length) * 1.5)));
  const raw = computeBins(samples, min, max, nBins);
  const peak = Math.max(...raw.map((b) => b.count), 1);

  const padX = 12;
  const padTop = 12;
  const axisH = 18; // room for the x-axis labels
  const padY = padTop;
  const plotW = cssW - padX * 2;
  const plotH = cssH - padTop - axisH;
  const gap = 2;
  const barW = (plotW - gap * (nBins - 1)) / nBins;

  // Horizontal gridlines (quartiles of the peak count).
  ctx.strokeStyle = "rgba(30,31,31,0.07)";
  ctx.lineWidth = 1;
  for (let g = 1; g <= 4; g++) {
    const y = padTop + plotH - (plotH * g) / 4;
    ctx.beginPath();
    ctx.moveTo(padX, y + 0.5);
    ctx.lineTo(cssW - padX, y + 0.5);
    ctx.stroke();
  }

  const bins: Bin[] = [];
  for (let i = 0; i < nBins; i++) {
    const b = raw[i]!;
    const h = (b.count / peak) * plotH;
    const x = padX + i * (barW + gap);
    const y = padTop + (plotH - h);
    ctx.fillStyle = i === highlight ? "#2f9e85" : "#51c0a7"; // darker accent on hover
    ctx.fillRect(x, y, Math.max(1, barW), h);
    bins.push({ x, w: Math.max(1, barW), count: b.count, lo: b.lo, hi: b.hi });
  }

  // Baseline axis.
  const baseY = padTop + plotH;
  ctx.strokeStyle = "rgba(30,31,31,0.18)";
  ctx.beginPath();
  ctx.moveTo(padX, baseY + 0.5);
  ctx.lineTo(cssW - padX, baseY + 0.5);
  ctx.stroke();

  // Mean marker — a dashed vertical line, since "mean of the chunks" is the headline-adjacent
  // quantity people look for in the distribution.
  const meanX = padX + ((mean - min) / span) * plotW;
  if (meanX >= padX && meanX <= cssW - padX) {
    ctx.strokeStyle = "rgba(30,31,31,0.45)";
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(meanX, padTop);
    ctx.lineTo(meanX, baseY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // X-axis labels: min, mean, max.
  ctx.fillStyle = "rgba(30,31,31,0.5)";
  ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText(fmt(min, 4), padX, baseY + 5);
  ctx.textAlign = "right";
  ctx.fillText(fmt(max, 4), cssW - padX, baseY + 5);
  if (meanX >= padX + 30 && meanX <= cssW - padX - 30) {
    ctx.textAlign = "center";
    ctx.fillText("mean " + fmt(mean, 4), meanX, baseY + 5);
  }
  ctx.textAlign = "left";

  return { bins, padX, padY, plotH, cssW, cssH };
}

// ── Repro claim ──────────────────────────────────────────────────────────────
function reproClaim(verifiable: boolean): string {
  return verifiable
    ? "Anyone who opens this run's permalink reproduces these exact bits on any machine — across browser engines."
    : "Anyone who opens this run's permalink reproduces these exact bits within the same browser engine. This kernel is marked unverifiable, so identical bits across different engines are not guaranteed.";
}

export function renderResults(el: HTMLElement, r: RunResult, permalink: string): void {
  const s = summarise(r.samples);
  const tier = r.verifiable
    ? `<span class="status-pill verified"><span class="dot"></span>Verifiable</span>`
    : `<span class="status-pill unverified"><span class="dot"></span>Engine-specific</span>`;

  el.innerHTML = `
    <div class="result-head">
      <div>
        <div class="result-value">${fmt(r.result, 8)}</div>
        <div class="result-unit">${escapeHtml(r.resultUnit)} ${info("result")}</div>
        ${
          s && s.ci95 > 0
            ? `<div class="result-ci">95% CI: ${fmt(r.result - s.ci95)} – ${fmt(r.result + s.ci95)} <span class="ci-pm">(±${fmt(s.ci95, 3)})</span> ${info("ci")}</div>`
            : ""
        }
      </div>
      <span class="tier-wrap">${tier} ${info("tier")}</span>
    </div>
    <div class="result-meta">
      N=${r.n.toLocaleString()} · seed="${escapeHtml(r.seed)}" · ${r.elapsedMs.toFixed(0)} ms · ${r.samples.length} chunks ${info("meta")}
    </div>

    ${
      s
        ? `<div class="stat-row">
            <div class="stat"><span class="s-label">mean ${info("mean")}</span><span class="s-value">${fmt(s.mean)}</span></div>
            <div class="stat"><span class="s-label">std error ${info("se")}</span><span class="s-value">${fmt(s.se, 4)}</span></div>
            <div class="stat"><span class="s-label">stdev ${info("stdev")}</span><span class="s-value">${fmt(s.stdev, 4)}</span></div>
            <div class="stat"><span class="s-label">p50 ${info("p50")}</span><span class="s-value">${fmt(s.p50)}</span></div>
            <div class="stat"><span class="s-label">min ${info("min")}</span><span class="s-value">${fmt(s.min)}</span></div>
            <div class="stat"><span class="s-label">max ${info("max")}</span><span class="s-value">${fmt(s.max)}</span></div>
          </div>`
        : ""
    }

    <div class="hist-head"><span class="hist-title">Per-chunk distribution</span> ${info("hist")}</div>
    <canvas class="hist-canvas" id="hist"></canvas>

    <div class="hash-row">
      <span class="hash-label">result bits ${info("resultBits")}</span>
      <span class="hash">${r.resultBits}</span>
    </div>
    <div class="hash-row">
      <span class="hash-label">kernel hash ${info("kernelHash")}</span>
      <span class="hash">${r.kernelHash}</span>
    </div>
    <div class="hash-row">
      <span class="hash-label">substrate ${info("substrate")}</span>
      <span class="hash">${r.triple.sdk}.${r.triple.shim}.${r.triple.fdlibm}</span>
    </div>

    <div class="repro-note">
      <span>🔒</span>
      <span>
        ${reproClaim(r.verifiable)}
        <button class="copy-link" id="copy-link">Copy permalink</button>
      </span>
    </div>
  `;

  // Info-dot tooltips are handled globally by ./tooltip (installed once at app boot); no
  // per-panel listener needed. showTip/hideTip below are used for the histogram bar hover.

  const canvas = el.querySelector<HTMLCanvasElement>("#hist");
  if (canvas && s) {
    let geom: HistGeom | null = null;
    let hovered = -1;
    const redraw = (hi: number) => {
      geom = drawHistogram(canvas, r.samples, s.mean, hi);
    };
    // Defer one frame so clientWidth is laid out before we size the backing store.
    requestAnimationFrame(() => redraw(-1));
    // Redraw crisply if the panel is resized (responsive width).
    const ro = new ResizeObserver(() => redraw(hovered));
    ro.observe(canvas);

    const binAt = (clientX: number): number => {
      if (!geom) return -1;
      const rect = canvas.getBoundingClientRect();
      const px = clientX - rect.left;
      for (let i = 0; i < geom.bins.length; i++) {
        const b = geom.bins[i]!;
        // Hit zone spans the bar plus half the gap on each side.
        if (px >= b.x - 1 && px <= b.x + b.w + 1) return i;
      }
      return -1;
    };

    canvas.addEventListener("mousemove", (e) => {
      const i = binAt(e.clientX);
      if (i !== hovered) {
        hovered = i;
        redraw(i);
      }
      if (i >= 0 && geom) {
        const b = geom.bins[i]!;
        const pct = ((b.count / r.samples.length) * 100).toFixed(0);
        showTip(
          `<div class="tt-title">${fmt(b.lo, 5)} – ${fmt(b.hi, 5)}</div>` +
            `<div class="tt-row"><b>${b.count}</b> chunk${b.count === 1 ? "" : "s"} (${pct}%)</div>`,
          e.clientX,
          e.clientY,
        );
      } else {
        hideTip();
      }
    });
    canvas.addEventListener("mouseleave", () => {
      hovered = -1;
      hideTip();
      redraw(-1);
    });
  }

  wireCopyButton(el.querySelector<HTMLButtonElement>("#copy-link"), permalink, "Copy permalink");
}

// Copy a permalink (origin + path + fragment) to the clipboard, with transient button feedback.
// Shared by the real result panel and the example empty state.
function wireCopyButton(btn: HTMLButtonElement | null, permalink: string, label: string): void {
  if (!btn) return;
  btn.addEventListener("click", async () => {
    const url = location.origin + location.pathname + permalink;
    try {
      await navigator.clipboard.writeText(url);
      btn.textContent = "Copied ✓";
      setTimeout(() => (btn.textContent = label), 1500);
    } catch {
      btn.textContent = "Copy failed";
    }
  });
}

// Result-panel empty state (rec #1/#7): shown before the first real run. Demonstrates the
// product's whole point — a permalink anyone can open to re-derive the exact same bits —
// using a *live* gallery permalink (v=1: #v=1&k=…&seed=…&n=…, see permalink.ts). The link is
// a deterministic string needing no prior run; opening it decodes-and-runs on load. We don't
// hardcode result numbers (they'd drift from the SDK); the honesty is that the link works.
const EXAMPLE = { kernel: "pi", seed: "experiment-42", n: 200_000 } as const;

export function renderResultEmptyState(el: HTMLElement): void {
  const permalink = `#v=1&k=${EXAMPLE.kernel}&seed=${encodeURIComponent(EXAMPLE.seed)}&n=${EXAMPLE.n}`;
  el.innerHTML = `
    <div class="result-empty">
      <div class="result-empty-lead">Press <strong>Run</strong> to compute a result — it appears here with a shareable permalink.</div>
      <div class="example-block">
        <div class="example-head"><span class="example-tag">example</span> a real, reproducible permalink</div>
        <code class="example-link">${escapeHtml(permalink)}</code>
        <div class="example-foot">
          <button class="copy-link" id="copy-example">Copy example link</button>
          <a class="example-open" href="${escapeHtml(permalink)}" target="_blank" rel="noopener">Open it →</a>
          <span class="muted">Open in any browser → it re-derives the exact same bits (runs π in your browser).</span>
        </div>
      </div>
    </div>
  `;
  wireCopyButton(el.querySelector<HTMLButtonElement>("#copy-example"), permalink, "Copy example link");
}
